/**
 * FrostedSwitchModal — Apple Home 風格毛玻璃 Switch/Outlet
 *
 * 設計語言：
 * - Green tint → Switch/Outlet 專用色調
 * - Tap to toggle ON/OFF — no drag
 * - ON: green fill from bottom ~40%, white icon
 * - OFF: small dark box at bottom with dimmed icon
 * - No text in track — pure visual (Apple Home style)
 * - Optional power metrics card (watt/voltage/current/energy)
 * - Icon picker in settings: Fan / Outlet / Light / Power
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedSwitchModal.css'

// ---------------------------------------------------------------------------
// Section definitions
// ---------------------------------------------------------------------------

export const SWITCH_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'toggle', label: 'Power Toggle', icon: 'power_settings_new', slots: [
    { id: 'toggle-track', label: 'Track', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'toggle-fill', label: 'ON Fill', editableProps: ['background'] },
  ]},
  { id: 'metrics', label: 'Power Metrics', icon: 'bolt', slots: [] },
]

const DEFAULT_SECTION_ORDER = SWITCH_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Icon options for settings
// ---------------------------------------------------------------------------

const ICON_OPTIONS = [
  { id: 'power', icon: 'power', label: 'Power' },
  { id: 'outlet', icon: 'outlet', label: 'Outlet' },
  { id: 'lightbulb', icon: 'lightbulb', label: 'Light' },
  { id: 'mode_fan', icon: 'mode_fan', label: 'Fan' },
] as const

const DEFAULT_ICON = 'power'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedSwitchModal({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  // ---- Context menu ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- Capabilities ----
  const hasPowerMetrics = useMemo(() => {
    if (!deviceState) return false
    // Check if device reports any power-related attributes
    return numAttr(deviceState, 'currentPowerW', 'watt', 'power') !== null
      || numAttr(deviceState, 'voltage', 'voltageV') !== null
      || numAttr(deviceState, 'current', 'currentA') !== null
      || numAttr(deviceState, 'energy', 'energyWh', 'totalEnergyKwh') !== null
  }, [deviceState])

  // ---- State from device ----
  const isOn = Boolean(getAttr(deviceState, 'on'))

  // Power metrics
  const powerW = numAttr(deviceState, 'currentPowerW', 'watt', 'power')
  const voltage = numAttr(deviceState, 'voltage', 'voltageV')
  const current = numAttr(deviceState, 'current', 'currentA')
  const energy = numAttr(deviceState, 'energy', 'energyWh', 'totalEnergyKwh')

  // ---- Settings ----
  const [showSettings, setShowSettings] = useState(false)
  const [selectedIcon, setSelectedIcon] = useState(DEFAULT_ICON)

  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])

  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) {
      setIsRenaming(false)
      return
    }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Optimistic power state ----
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayPower = pendingPower ?? isOn

  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])

  // ---- API caller ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower
    setPendingPower(next)
    execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  // ---- Derived ----
  const stateClass = displayPower ? 'switch-frosted--on' : 'switch-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ---- Render sections ----
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'toggle':
        return (
          <div key="toggle" className={`switch-frosted__toggle-area${hl('toggle')}`} data-modal-section="toggle">
            <div className="switch-frosted__track" onClick={togglePower}>
              <div className="switch-frosted__fill" />
              <div className="switch-frosted__bar" />
              <div className="switch-frosted__icon-bg" />
              <div className="switch-frosted__icon-area">
                <span className="switch-frosted__icon material-symbols-outlined">{selectedIcon}</span>
              </div>
            </div>
          </div>
        )

      case 'metrics':
        if (!hasPowerMetrics) return null
        return (
          <div key="metrics" className={`switch-frosted__metrics${hl('metrics')}`} data-modal-section="metrics">
            <div className="switch-frosted__metrics-title">
              <span className="material-symbols-outlined">bolt</span> Power Monitor
            </div>
            <div className="switch-frosted__metrics-grid">
              {powerW !== null && (
                <div className="switch-frosted__metric">
                  <div className="switch-frosted__metric-label">Power</div>
                  <div className="switch-frosted__metric-value">
                    {powerW.toFixed(0)}<span className="switch-frosted__metric-unit">W</span>
                  </div>
                </div>
              )}
              {voltage !== null && (
                <div className="switch-frosted__metric">
                  <div className="switch-frosted__metric-label">Voltage</div>
                  <div className="switch-frosted__metric-value">
                    {voltage.toFixed(0)}<span className="switch-frosted__metric-unit">V</span>
                  </div>
                </div>
              )}
              {current !== null && (
                <div className="switch-frosted__metric">
                  <div className="switch-frosted__metric-label">Current</div>
                  <div className="switch-frosted__metric-value">
                    {current.toFixed(2)}<span className="switch-frosted__metric-unit">A</span>
                  </div>
                </div>
              )}
              {energy !== null && (
                <div className="switch-frosted__metric">
                  <div className="switch-frosted__metric-label">Energy</div>
                  <div className="switch-frosted__metric-value">
                    {energy >= 1000 ? (energy / 1000).toFixed(1) : energy.toFixed(0)}
                    <span className="switch-frosted__metric-unit">{energy >= 1000 ? 'kWh' : 'Wh'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`switch-frosted ${stateClass}`}
      data-modal-template-id="switch-frosted"
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      {/* Header */}
      <div className="switch-frosted__header" data-modal-section="_modal">
        <button className="switch-frosted__back" onClick={onClose} aria-label="Back">&lsaquo;</button>
        <span className="switch-frosted__name">{displayName}</span>
      </div>

      {/* Status */}
      <div className="switch-frosted__status">{displayPower ? 'ON' : 'OFF'}</div>

      {/* Sections */}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings */}
      <button
        className="switch-frosted__settings-btn"
        onClick={() => setShowSettings(true)}
        aria-label="Settings"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="switch-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="switch-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="switch-frosted__settings-header">
              <span className="switch-frosted__settings-title">Settings</span>
              <button className="switch-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            {/* Icon Picker */}
            <div className="switch-frosted__settings-section">
              <div className="switch-frosted__settings-section-title">Icon</div>
              <div className="switch-frosted__icon-picker">
                {ICON_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    className={`switch-frosted__icon-option${selectedIcon === opt.icon ? ' active' : ''}`}
                    onClick={() => setSelectedIcon(opt.icon)}
                    title={opt.label}
                  >
                    <span className="material-symbols-outlined">{opt.icon}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Rename */}
            <div className="switch-frosted__settings-section">
              <div className="switch-frosted__settings-section-title">Device Name</div>
              {isRenaming ? (
                <div className="switch-frosted__settings-row">
                  <input
                    ref={renameInputRef}
                    className="switch-frosted__rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename}
                    maxLength={40}
                  />
                </div>
              ) : (
                <div className="switch-frosted__settings-row">
                  <span className="switch-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>
                    {displayName}
                  </span>
                  <button className="switch-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>

            {/* Device Info */}
            <div className="switch-frosted__settings-section">
              <div className="switch-frosted__settings-section-title">Device Info</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="switch-frosted__settings-row">
                  <span className="switch-frosted__settings-label">{item.label}</span>
                  <span className="switch-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button
                      className="switch-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)}
                      title="Copy"
                    >
                      📋
                      {copiedField === item.label && (
                        <span className="switch-frosted__copied-toast">Copied</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
